import React from 'react';
import {
  Box, Button, Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { TotalPrepayProps } from '../../types/loanPayment';
import styles from './styles';
import AlertDialog from '../../components/AlertDialog';
import ActiveInjectionAlert from './ActiveInjectionAlert';
import { parseAmountWithCurrency } from '../../utils/helpers';

export default function TotalPrepay(props: TotalPrepayProps) {
  const {
    loanId,
    paidAmount,
    unpaidTotalAmount,
    simulationData,
    simulationError,
    lastPrepayRequestData,
    totalToDeposit,
    collectionServiceUrl,
    handleArchivePrepayRequest,
    hasActiveInjections,
    pageCategory,
  } = props;

  const history = useHistory();
  const [showActiveInjectionAlert, setShowActiveInjectionAlert] = React.useState(false);

  const savings = (unpaidTotalAmount - simulationData.totalToDeposit);

  const goToTotalPrepay = () => {
    setTimeout(() => history.push({
      pathname: `/account/total-prepay/${loanId}`,
      state: {
        unpaidAmount: unpaidTotalAmount,
        paidAmount,
        delinquencyDebt: simulationData.delinquencyDebt,
        interestAmount: simulationData.interestAmount,
        prepayComission: simulationData.prepayComission,
        totalToDeposit: simulationData.totalToDeposit,
        unpaidCapital: simulationData.unpaidCapital,
      },
    }), 500);
  };

  const goToTotalPrepayOverview = () => {
    if (collectionServiceUrl) {
      window.location.href = collectionServiceUrl;
    }
  };

  const handlePrepayLoan = () => {
    if (hasActiveInjections) {
      setShowActiveInjectionAlert(true);
    } else {
      goToTotalPrepayOverview();
    }
  };

  if (lastPrepayRequestData) {
    return (
      <>
        <Box sx={styles.loanPayoutContainer}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }} gutterBottom>
            Tienes una solicitud de prepago total pendiente
          </Typography>
          <Typography variant="caption">
            Esta solicitud tiene una vigencia hasta las 23:59 hrs de hoy.
          </Typography>
        </Box>
        <Box sx={styles.loanPayoutContainer}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={styles.loanPayoutButton}
            onClick={handlePrepayLoan}
            disabled={!collectionServiceUrl}
          >
            Prepagar crédito
          </Button>
          {!collectionServiceUrl && (
            <Typography
              color="error"
              variant="caption"
              gutterBottom
            >
              Error al cargar el enlace de pago. Por favor contáctanos.
            </Typography>
          )}
          <Typography
            variant="caption"
            fontWeight="bold"
          >
            Total a pagar:
            <Typography
              variant="caption"
              fontWeight="bold"
              color="secondary"
              component="span"
            >
              {' '}
              {parseAmountWithCurrency(lastPrepayRequestData.currency, totalToDeposit)}
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            fontWeight="bold"
          >
            Te ahorras:
            <Typography
              variant="caption"
              fontWeight="bold"
              color="secondary"
              component="span"
            >
              {' '}
              {parseAmountWithCurrency(
                lastPrepayRequestData.currency,
                unpaidTotalAmount - totalToDeposit,
              )}
            </Typography>
          </Typography>
        </Box>
        <Box sx={styles.loanPayoutContainer}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            sx={styles.loanPayoutButton}
            onClick={handleArchivePrepayRequest}
            disabled={!lastPrepayRequestData}
          >
            Cancelar solicitud
          </Button>
          <Typography color="primary" variant="caption" sx={{ marginBottom: 0.5 }}>
            Si cancelas la solicitud,
            podrás volver a realizar una nueva solicitud de prepago cuando quieras.
          </Typography>
          <AlertDialog
            isTriggered={showActiveInjectionAlert}
            onCloseAction={() => setShowActiveInjectionAlert(false)}
            dialogContent={<ActiveInjectionAlert showAlert />}
            action={goToTotalPrepayOverview}
            pageCall={{
              category: pageCategory,
              name: 'PREPAGO PENDIENTE - COBRO EN PROCESO',
            }}
            maxWidth="xs"
            hideLogo
            button={<div />}
          />
        </Box>
      </>
    );
  }

  let body;
  if (simulationError) {
    body = (
      <Typography variant="caption" color="error">
        Ha ocurrido un error. Por favor contáctanos.
      </Typography>
    );
  } else if (savings >= 0) {
    body = (
      <>
        <Typography
          variant="caption"
          fontWeight="bold"
        >
          Total a pagar:
          <Typography
            variant="caption"
            fontWeight="bold"
            color="secondary"
            component="span"
          >
            {' '}
            {parseAmountWithCurrency(simulationData.currency, simulationData.totalToDeposit)}
          </Typography>
        </Typography>
        <Typography
          variant="caption"
          fontWeight="bold"
        >
          Te ahorras:
          <Typography
            variant="caption"
            fontWeight="bold"
            color="secondary"
            component="span"
          >
            {' '}
            {parseAmountWithCurrency(simulationData.currency, savings)}
          </Typography>
        </Typography>
      </>
    );
  } else if (savings < 0) {
    body = (
      <Typography variant="caption" color="primary">
        Hemos deshabilitado esta opción porque al día de hoy no te conviene prepagar
        ya que te saldría más caro que continuar con tus pagos.
      </Typography>
    );
  } else {
    body = (
      <Typography variant="caption" color="secondary">
        No puedes prepagar este tipo de crédito.
      </Typography>
    );
  }

  return (
    <Box sx={styles.loanPayoutContainer}>
      <Button
        variant="outlined"
        color="primary"
        size="large"
        sx={styles.loanPayoutButton}
        onClick={goToTotalPrepay}
        disabled={savings < 0 || !!simulationError}
      >
        Prepagar crédito
      </Button>
      {body}
    </Box>
  );
}
